/**
 * SubmissionsPage — Per-rubric submissions list.
 *  - Faculty: see all submissions, click to grade
 *  - Student: see only their own + a textarea to submit new work
 */
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import {
  getRubric, listSubmissionsForRubric, createSubmission,
} from '../lib/apiClient';
import { useAuth } from '../context/AuthContext';
import CurvePanel from '../components/CurvePanel';
import DiscussionAutoScoreModal from '../components/DiscussionAutoScoreModal';

const GRADER_ROLES = ['developer', 'institution_admin', 'teacher'];

const StatusBadge = ({ sub }) => {
  if (sub.finalized_at) {
    const curved = sub.curved_total != null && sub.curved_total !== sub.final_total;
    return (
      <span className="badge-published" data-testid="status-finalized">
        ✓ Finalized — {sub.final_total}
        {curved && <> · curved <strong>{sub.curved_total}</strong></>}
      </span>
    );
  }
  if ((sub.ai_drafts || []).length > 0) {
    return <span className="badge-draft" data-testid="status-ai-drafted">AI drafted</span>;
  }
  return <span className="badge-draft" data-testid="status-ungraded">Ungraded</span>;
};

const SubmissionsPage = () => {
  const { rubricId } = useParams();
  const { user } = useAuth();
  const canGrade = user && GRADER_ROLES.includes(user.role);

  const [rubric, setRubric] = useState(null);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Student submit form
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitOk, setSubmitOk] = useState(false);

  // Faculty: auto-score modal
  const [showAutoScore, setShowAutoScore] = useState(false);

  const refresh = () =>
    Promise.all([getRubric(rubricId), listSubmissionsForRubric(rubricId)])
      .then(([r, s]) => {
        setRubric(r);
        setSubs(s);
      })
      .catch((e) => setError(e.response?.data?.detail || 'Load failed'))
      .finally(() => setLoading(false));

  useEffect(() => {
    refresh();
     
  }, [rubricId]);

  const submit = async () => {
    if (!text.trim()) return;
    setSubmitting(true);
    setSubmitOk(false);
    try {
      await createSubmission({
        rubric_id: rubricId,
        module_id: rubric?.module_id || null,
        topic_id: rubric?.topic_id || null,
        content_text: text,
      });
      setText('');
      setSubmitOk(true);
      await refresh();
    } catch (e) {
      setError(e.response?.data?.detail || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (<div className="min-h-screen"><Navbar /><div className="p-8 text-gray-500">Loading…</div></div>);
  if (error && !rubric) return (<div className="min-h-screen"><Navbar /><div className="p-8 text-red-600">{error}</div></div>);

  return (
    <div className="min-h-screen" data-testid="submissions-page">
      <Navbar />
      <main className="max-w-5xl mx-auto px-6 py-10">
        <div className="mb-2">
          <Link to="/rubrics" className="text-sm text-gray-500 hover:underline">← All rubrics</Link>
        </div>
        <h1 className="text-3xl font-bold text-gray-900" data-testid="rubric-title-header">{rubric.title}</h1>
        <p className="text-gray-500 mt-1">{rubric.description}</p>
        <div className="text-xs text-gray-500 mt-2" data-testid="rubric-meta">
          {rubric.criteria.length} criteria · weighted max{' '}
          <span className="font-mono">{rubric.total_max_points}</span>
        </div>

        {/* Rubric criteria preview (visible to everyone — transparency) */}
        <section className="card p-4 mt-6">
          <h2 className="font-bold mb-2">Criteria</h2>
          <ul className="space-y-1 text-sm">
            {rubric.criteria.map((c) => (
              <li key={c.id} data-testid={`crit-preview-${c.id}`}>
                <span className="font-semibold">{c.name}</span>{' '}
                <span className="text-gray-500">— up to {c.max_points} pts (weight ×{c.weight})</span>
                {c.description && <div className="text-xs text-gray-500 ml-4">{c.description}</div>}
              </li>
            ))}
          </ul>
        </section>

        {/* Phase 1.7d — Faculty tools: auto-score discussion + grade curve */}
        {canGrade && (
          <section className="mt-6 flex flex-wrap gap-2" data-testid="faculty-tools">
            <button
              onClick={() => setShowAutoScore(true)}
              className="btn-secondary text-sm"
              data-testid="open-auto-score-btn"
              title="Pick a discussion thread, AI-score every participant's posts at once."
            >
              💬 Auto-score a discussion
            </button>
          </section>
        )}
        {canGrade && <CurvePanel rubric={rubric} onApplied={refresh} />}

        {/* Student submit panel */}
        {!canGrade && (
          <section className="card p-4 mt-6" data-testid="student-submit-panel">
            <h2 className="font-bold mb-2">Your Submission</h2>
            <textarea
              className="input-field"
              rows={6}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Type your answer here…"
              data-testid="submission-text-input"
            />
            <div className="flex justify-between items-center mt-2">
              {submitOk && <div className="text-sm text-green-600" data-testid="submit-success">Submitted ✓</div>}
              <button
                onClick={submit}
                disabled={!text.trim() || submitting}
                className="btn-primary ml-auto"
                data-testid="submit-btn"
              >
                {submitting ? 'Submitting…' : 'Submit'}
              </button>
            </div>
          </section>
        )}

        {/* Submissions list */}
        <section className="mt-8" data-testid="submissions-list-section">
          <h2 className="text-xl font-bold mb-2">
            {canGrade ? `Submissions (${subs.length})` : 'Your submissions'}
          </h2>
          {subs.length === 0 ? (
            <div className="card p-6 text-gray-500 text-center" data-testid="no-submissions">
              {canGrade ? 'No submissions yet.' : 'You have not submitted yet.'}
            </div>
          ) : (
            <div className="space-y-2" data-testid="submissions-list">
              {subs.map((s) => (
                <Link
                  key={s.id}
                  to={`/submissions/${s.id}`}
                  className="card p-3 flex items-center justify-between hover:shadow"
                  data-testid={`sub-row-${s.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold">{s.student_name}</div>
                    <div className="text-xs text-gray-500 mt-1">
                      Submitted {new Date(s.submitted_at).toLocaleString()}
                      {s.content_text && <> · {s.content_text.length} chars</>}
                    </div>
                  </div>
                  <StatusBadge sub={s} />
                </Link>
              ))}
            </div>
          )}
        </section>
      </main>
      {showAutoScore && (
        <DiscussionAutoScoreModal
          rubric={rubric}
          onClose={() => setShowAutoScore(false)}
          onCompleted={refresh}
        />
      )}
    </div>
  );
};

export default SubmissionsPage;
